import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { io, Socket } from 'socket.io-client';
import { marked } from 'marked';
import { CookiesService } from './cookies-service';

export namespace WidgetModel {
  export enum Author {
    USER = 'user',
    ASSISTANT = 'assistant',
  }

  export enum MessageType {
    CORE_MESSAGE = 'CORE_MESSAGE',
    ADDITIONAL_SYSTEM_MESSAGE = 'ADDITIONAL_SYSTEM_MESSAGE',
    TYPING = 'TYPING',
    ERROR = 'ERROR',
    INFO_MESSAGE = 'INFO_MESSAGE',
  }

  export enum ChatType {
    WIDGET_CHAT = 'WIDGET_CHAT',
    INTERNAL_CHAT = 'INTERNAL_CHAT',
  }

  export interface Message {
    author: Author;
    text: string;
    final?: boolean;
    trimmed?: boolean;
    type: MessageType;
    chat_session_id?: number;
  }

  export interface IFile {
    id: number;
    created_at: string;
    modified_at: string;
    original_name: string;
    current_name: string;
    size: number;
    path: string;
    file_ingested_into_vector_db: boolean;
  }

  export interface Chat {
    id?: number;
    created_at?: string;
    modified_at?: string;
    name?: string;
    files?: IFile[];
    messages: Message[];
    description?: string;
    shared: boolean;
  }

  export interface BasicChatDTO {
    id: number;
    created_at: string;
    modified_at: string;
    name: string;
    description: string;
    welcome_message: string | null;
    info_message: string | null;
    type: ChatType;
    is_active_widget: boolean;
    maintanance_mode: boolean;
  }

  export interface ChatSessionDTO {
    id: number;
    created_at: string;
    modified_at: string;
    title: string;
    is_active: boolean;
    chat: BasicChatDTO;
    active_file_ids: number[];
    user: {
      id: number;
      first_name: string;
      last_name: string;
      login: string;
      email: string;
      role: string;
    };
    messages: Message[];
    is_rated?: boolean;
  }
}

@customElement('chatbot-widget')
export class ChatbotWidget extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: String }) domainapiurl = 'http://localhost:3000';
  @property({ type: String }) welcomemessage = 'W czym mogę pomóc?';
  @property({ type: String }) infomessage = '';

  @state() private messageInput = '';
  @state() private loading = false;
  @state() private chatDataLoading = false;
  @state() private chat: WidgetModel.Chat = { messages: [], shared: false };
  @state() private socket: Socket | null = null;
  @state() private chat_session_id: number | null = null;
  @state() private maintananceMode = false;

  @state() private isPulsing = false;
  @state() private showInfoBox = false;
  @state() private fetchedInfoMessage: string = '';

  @state() private typingPhase: 1 | 2 | 3 | 4 | 5 | 6 | null = null;
  @state() private typingTimer: any = null;
  @state() private typingTimer2: any = null;
  @state() private typingTimer3: any = null;
  @state() private typingTimer4: any = null;
  @state() private typingTimer5: any = null;
  @state() private typingText: string = '';

  @state() private responseTimeoutTimer: any = null;
  @state() private hasTimeoutError = false;

  @state() private isScrolledToBottom = true;
  @state() private isScrolledToTop = false;

  @state() private sessionRated = false;
  @state() private showRatingInChat = false;
  @state() private showRatingPopup = false;
  @state() private questionAnswerCount = 0;
  @state() private selectedRating = 0;
  @state() private ratingSubmitted = false;
  @state() private ratingThankYouMessage = false;
  @state() private ratingInChatHiding = false;
  @state() private ratingPopupHiding = false;
  @state() private ratingInChatDismissed = false;

  private _previousDomainApiUrl: string = '';
  private cookiesService = new CookiesService();

  private pulseInterval: any = null;
  private ctaTimeout: any = null;

  // Heartbeat mechanism
  private heartbeatInterval: any = null;
  private heartbeatTimeout: any = null;

  private renderMarkdown(text: string): string {
    try {
      const processedText = text.replace(/\\n/g, '\n');

      return marked(processedText, {
        breaks: true,
        gfm: true,
      }) as string;
    } catch (error) {
      console.error('Błąd renderowania markdown:', error);
      return text;
    }
  }

  static styles = css`
    :host {
      --widget-primary: #fd8204;
      --widget-primary-light: #FD9935;
      --widget-primary-dark: #DE7002;
      --widget-secondary: #373c3c;
      --widget-message-user: #fff;
      --widget-font-light: #fff;
      --widget-font-dark: #262626;
      --widget-background-light: #FFF;
      display: block;
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 999990;
      font-family: inherit;
    }

    .widget-button {
      width: 100%;
      height: 60px;
      border-radius: 30px;
      background-color: var(--widget-primary);
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      transition: all 0.3s ease;
      position: relative;
      z-index: 999999;
    }

    .widget-button-content {
      padding: 8px 24px 8px 8px;
      display: flex;
      justify-content: center;
      align-items: center;
      column-gap: 8px;
    }

    .widget-button-icon-wrapper {
        background-color: var(--widget-background-light);
        display: flex;
        justify-content: center;
        align-items: center;
        width: 44px;
        height: 44px;
        border-radius: 22px;
    }

    .widget-button-text {
        font-size: 14px;
        color: white;
        font-weight: 600;
        line-height: 20px;
        letter-spacing: 0%;
    }
      
    /* Safari SVG compatibility fixes */
    svg {
      display: block;
      -webkit-transform: translateZ(0);
      transform: translateZ(0);
    }

    .widget-button:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
    }

    .widget-button:hover.pulsing {
      animation: none;
    }

    .widget-button.pulsing {
      animation: attention-pulse 0.6s ease-in-out;
    }

    .info-box {
      position: absolute;
      bottom: 80px;
      right: 0;
      width: 500px;
      max-width: calc(100vw - 40px);
      background-color: rgba(51, 50, 50, 0.8);
      border: 1px solid rgba(118, 114, 113, 1);
      backdrop-filter: blur(2px);
      border-radius: 24px;
      padding: 16px 24px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      z-index: 999998;
      opacity: 0;
      visibility: hidden;
      transform: translateY(30px) scale(0.9);
      transition: none;
      color: white;
      font-size: 16px;
      line-height: 24px;
      font-weight: 400;
    }

    .info-box-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
    }

    .info-box.visible {
      opacity: 1;
      visibility: visible;
      animation: infoBoxSlideUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    @keyframes infoBoxSlideUp {
      0% {
        opacity: 0;
        transform: translateY(30px) scale(0.9);
      }
      60% {
        opacity: 1;
        transform: translateY(-5px) scale(1.02);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    @keyframes fadeOut {
      0% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
      100% {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
        visibility: hidden;
      }
    }

    .info-box-close {
      float: right;
      margin: 2px 0 4px 6px;
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      padding: 4px;
      border-radius: 4px;
    }

    .info-box-content {
      color: rgba(255, 255, 255, 0.9);
    }

    @keyframes attention-pulse {
      0% { transform: scale(1); }
      25% { transform: scale(1.05); }
      50% { transform: scale(1); }
      75% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }

    .chat {
      position: absolute;
      bottom: 80px;
      right: 0;
      width: 500px;
      max-width: calc(100vw - 40px);
      height: 737px;
      max-height: 70vh;
      background-color: rgba(51, 50, 50, 0.8);
      border: 1px solid rgba(118, 114, 113, 1);
      backdrop-filter: blur(2px);
      border-radius: 24px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      transform-origin: bottom right;
      transition: transform 0.3s ease, opacity 0.3s ease;
      transform: scale(0);
      opacity: 0;
    }

    .chat.open {
      transform: scale(1);
      opacity: 1;
    }

    .chat-header {
      font-size: 20px;
      color: white;
      padding: 0 8px;
      font-weight: 600;
      display: flex;
      align-items: center;
      line-height: 1.3;
      height: 76px;
      width: 100%;
      z-index: 1000;
    }

    .close-button {
      cursor: pointer;
      padding: 12px;
    }
      
    .chat-header-content {
      display: flex;
      align-items: center;
      column-gap: 12px;
    }

    .chat-header-icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px;
      background-color: var(--widget-background-light);
      border-radius: 50%;
    }
      
    .chat-header-title-wrapper {
      display: flex;
      flex-direction: column;
      row-gap: 2px;
    }

    .chat-header-name {
      font-size: 16px;
      line-height: 20px;
      font-weight: 600;
      color: white;
    }

    .chat-header-availability {
      display: flex;
      column-gap: 4px;
      align-items: center;
    }

    .chat-header-availability-icon {
      width: 8px;
      height: 8px;
      background-color: #7DDE86;
      border-radius: 4px;
    }

    .chat-header-availability-text {
      font-size: 12px;
      line-height: 16px;
      font-weight: 400;
    }

    .chat-messages-wrapper {
      flex: 1;
      position: relative;
      overflow: hidden;
    }

    .chat-messages {
      height: 100%;
      overflow-y: auto;
      padding: 0 24px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      scroll-behavior: smooth;
      -webkit-mask-image: linear-gradient(
              to bottom,
              transparent 0px,
              rgba(0, 0, 0, 0.05) 8px,
              rgba(0, 0, 0, 0.2) 16px,
              rgba(0, 0, 0, 0.4) 20px,
              rgba(0, 0, 0, 0.6) 24px,
              rgba(0, 0, 0, 0.8) 28px,
              black 30px
      );
      mask-image: linear-gradient(
              to bottom,
              transparent 0px,
              rgba(0, 0, 0, 0.05) 8px,
              rgba(0, 0, 0, 0.2) 16px,
              rgba(0, 0, 0, 0.4) 20px,
              rgba(0, 0, 0, 0.6) 24px,
              rgba(0, 0, 0, 0.8) 28px,
              black 30px
      );
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      transition: -webkit-mask-image 0.3s ease, mask-image 0.3s ease;
    }

    .chat-messages-spacer {
      flex: 1 0 auto;
      min-height: 0;
    }

    .chat-messages.not-at-bottom {
      -webkit-mask-image: linear-gradient(
              to bottom,
              transparent 0px,
              rgba(0, 0, 0, 0.05) 8px,
              rgba(0, 0, 0, 0.2) 16px,
              rgba(0, 0, 0, 0.4) 20px,
              rgba(0, 0, 0, 0.6) 24px,
              rgba(0, 0, 0, 0.8) 28px,
              black 30px,
              black 75%,
              rgba(0, 0, 0, 0.8) 78%,
              rgba(0, 0, 0, 0.6) 80%,
              rgba(0, 0, 0, 0.4) 82%,
              rgba(0, 0, 0, 0.2) 85%,
              rgba(0, 0, 0, 0.05) 90%,
              transparent 96%
      );
      mask-image: linear-gradient(
              to bottom,
              transparent 0px,
              rgba(0, 0, 0, 0.05) 8px,
              rgba(0, 0, 0, 0.2) 16px,
              rgba(0, 0, 0, 0.4) 20px,
              rgba(0, 0, 0, 0.6) 24px,
              rgba(0, 0, 0, 0.8) 28px,
              black 30px,
              black 75%,
              rgba(0, 0, 0, 0.8) 78%,
              rgba(0, 0, 0, 0.6) 80%,
              rgba(0, 0, 0, 0.4) 82%,
              rgba(0, 0, 0, 0.2) 85%,
              rgba(0, 0, 0, 0.05) 90%,
              transparent 96%
      );
    }

    .chat-messages.at-top {
      -webkit-mask-image: linear-gradient(
              to bottom,
              black 75%,
              rgba(0, 0, 0, 0.8) 78%,
              rgba(0, 0, 0, 0.6) 80%,
              rgba(0, 0, 0, 0.4) 82%,
              rgba(0, 0, 0, 0.2) 85%,
              rgba(0, 0, 0, 0.05) 90%,
              transparent 96%
      );
      mask-image: linear-gradient(
              to bottom,
              black 75%,
              rgba(0, 0, 0, 0.8) 78%,
              rgba(0, 0, 0, 0.6) 80%,
              rgba(0, 0, 0, 0.4) 82%,
              rgba(0, 0, 0, 0.2) 85%,
              rgba(0, 0, 0, 0.05) 90%,
              transparent 96%
      );
    }

    .message {
      font-size: 16px;
      line-height: 24px;
      padding: 6px 0;
      border-radius: 20px;
      margin-bottom: 5px;
      word-wrap: break-word;
      position: relative;
      font-weight: 400;
    }

    .message p {
      margin: 0;
    }

    .message-container {
      display: flex;
      align-items: flex-end;
      position: relative;
    }

    .message-container.user {
      flex-direction: row-reverse;
    }

    .message.user {
      align-self: flex-end;
      background-color: var(--widget-background-light);
      color: #000;
      padding: 6px 12px;
    }

    .message.assistant {
      align-self: flex-start;
      color: white;
    }

    .message.info {
      align-self: flex-start;
      color: white;
      max-width: 85%;
    }
      
    .message.info a {
        color: white;
    }

    .message.error {
      align-self: flex-start;
      background-color: var(--widget-secondary);
      color: var(--widget-font-light);
      border-bottom-left-radius: 0;
      display: flex;
      column-gap: 10px;
      align-items: center;
    }

    .message.error svg {
      width: 32px;
      height: 32px;
    }

    .message.error .content {
      width: fit-content;
    }

    .scroll-to-bottom-button {
      position: absolute;
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%);
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background-color: var(--widget-background-light);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
    }

    .scroll-to-bottom-button.visible {
      opacity: 1;
      visibility: visible;
    }

    .scroll-to-bottom-button:hover {
      transform: translateX(-50%) scale(1.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    }

    .chat-input {
      display: flex;
      padding: 10px 24px 0;
      position: relative;
      column-gap: 16px;
    }

    .chat-input-wrapper {
      display: flex;
      width: 100%;
      position: relative;
    }

    .chat-input input {
      flex: 1;
      height: 26px;
      padding: 8px 20px;
      border: 1px solid #767271;
      border-radius: 26px;
      outline: none;
      font-weight: 400;
      font-size: 16px;
    }

    .send-button {
      background-color: var(--widget-primary);
      color: white;
      border: none;
      padding: 12px;
      border-radius: 22px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
    }
      
    .send-button:disabled {
        cursor: not-allowed;
    }
    
    .info-button {
      background: none;
      border: none;
      color: #888;
      cursor: pointer;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      position: absolute;
      bottom: 2px;
      right: 0;
      font-size: 14px;
      z-index: 1;
    }

    .info-button:hover {
      color: var(--widget-primary);
    }

    .loading {
      display: flex;
      align-items: center;
      padding-left: 5px;
    }

    .typing-placeholder {
      font-size: 14px;
      font-style: italic;
      padding: 12px 0;
      color: rgba(255, 255, 255, 0.7);
      animation: typingGlow 2s ease-in-out infinite;
      text-shadow: 0 0 10px rgba(255, 255, 255, 0);
    }

    @keyframes typingGlow {
      0%, 100% {
        opacity: 0.5;
        text-shadow: 0 0 5px rgba(255, 255, 255, 0);
        color: rgba(255, 255, 255, 0.5);
      }
      50% {
        opacity: 1;
        text-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
        color: rgba(255, 255, 255, 0.95);
      }
    }

    .disclaimer {
      color: #A4A4A4;
      font-size: 12px;
      padding: 8px 24px;
      line-height: 1.2;
    }

    .footer {
      color: #A4A4A4;
      font-size: 10px;
      padding: 0 24px 32px;
      line-height: 1.2;
    }

    .footer-link {
      text-decoration: none;
      color: #A4A4A4;
    }

    @keyframes pulse {
      0%,
      100% {
        transform: scale(0.8);
        opacity: 0.5;
      }
      50% {
        transform: scale(1.2);
        opacity: 1;
      }
    }

    .chat-data-loading {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 100%;
      padding: 40px 20px;
      text-align: center;
    }

    .chat-data-loading-spinner {
      display: flex;
      align-items: center;
      margin-bottom: 15px;
    }

    .chat-data-loading-spinner span {
      width: 12px;
      height: 12px;
      margin: 0 3px;
      background-color: white;
      border-radius: 50%;
      display: inline-block;
      animation: chat-loading-pulse 1.5s infinite ease-in-out;
    }

    .chat-data-loading-spinner span:nth-child(2) {
      animation-delay: 0.2s;
    }

    .chat-data-loading-spinner span:nth-child(3) {
      animation-delay: 0.4s;
    }

    .chat-data-loading-text {
      color: var(--widget-secondary);
      font-size: 14px;
      font-weight: 500;
    }

    @keyframes chat-loading-pulse {
      0%,
      100% {
        transform: scale(0.8);
        opacity: 0.3;
      }
      50% {
        transform: scale(1.2);
        opacity: 1;
      }
    }

    @media (max-width: 1920px) {
      .info-box {
        font-size: 16px;
        line-height: 24px;
        font-weight: 400;
      }

      .info-box-close {
        margin: 2px 0 4px 6px;
      }

      .chat {
        height: 800px;
        max-height: 75vh;
      }

      .chat-header {
        font-size: 20px;
        padding: 0 8px;
        line-height: 1.3;
        height: 76px;
      }

      .close-button {
        padding: 12px;
      }
        
      .chat-header-content {
        column-gap: 12px;
      }

      .chat-header-icon-wrapper {
        padding: 12px;
      }

      .chat-header-name {
        font-size: 16px;
        line-height: 20px;
      }

      .chat-header-availability {
        column-gap: 4px;
      }

      .chat-header-availability-icon {
        width: 8px;
        height: 8px;
      }

      .chat-header-availability-text {
        font-size: 12px;
        line-height: 16px;
      }

      .chat-messages {
        padding: 0 24px;
        gap: 10px;
        -webkit-mask-image: linear-gradient(
                to bottom,
                transparent 0px,
                rgba(0, 0, 0, 0.05) 6px,
                rgba(0, 0, 0, 0.2) 12px,
                rgba(0, 0, 0, 0.4) 15px,
                rgba(0, 0, 0, 0.6) 18px,
                rgba(0, 0, 0, 0.8) 21px,
                black 24px
        );
        mask-image: linear-gradient(
                to bottom,
                transparent 0px,
                rgba(0, 0, 0, 0.05) 6px,
                rgba(0, 0, 0, 0.2) 12px,
                rgba(0, 0, 0, 0.4) 15px,
                rgba(0, 0, 0, 0.6) 18px,
                rgba(0, 0, 0, 0.8) 21px,
                black 24px
        );
      }

      .chat-messages.not-at-bottom {
        -webkit-mask-image: linear-gradient(
                to bottom,
                transparent 0px,
                rgba(0, 0, 0, 0.05) 6px,
                rgba(0, 0, 0, 0.2) 12px,
                rgba(0, 0, 0, 0.4) 15px,
                rgba(0, 0, 0, 0.6) 18px,
                rgba(0, 0, 0, 0.8) 21px,
                black 24px,
                black 75%,
                rgba(0, 0, 0, 0.8) 78%,
                rgba(0, 0, 0, 0.6) 80%,
                rgba(0, 0, 0, 0.4) 82%,
                rgba(0, 0, 0, 0.2) 85%,
                rgba(0, 0, 0, 0.05) 90%,
                transparent 96%
        );
        mask-image: linear-gradient(
                to bottom,
                transparent 0px,
                rgba(0, 0, 0, 0.05) 6px,
                rgba(0, 0, 0, 0.2) 12px,
                rgba(0, 0, 0, 0.4) 15px,
                rgba(0, 0, 0, 0.6) 18px,
                rgba(0, 0, 0, 0.8) 21px,
                black 24px,
                black 75%,
                rgba(0, 0, 0, 0.8) 78%,
                rgba(0, 0, 0, 0.6) 80%,
                rgba(0, 0, 0, 0.4) 82%,
                rgba(0, 0, 0, 0.2) 85%,
                rgba(0, 0, 0, 0.05) 90%,
                transparent 96%
        );
      }

      .chat-messages.at-top {
        -webkit-mask-image: linear-gradient(
                to bottom,
                black 75%,
                rgba(0, 0, 0, 0.8) 78%,
                rgba(0, 0, 0, 0.6) 80%,
                rgba(0, 0, 0, 0.4) 82%,
                rgba(0, 0, 0, 0.2) 85%,
                rgba(0, 0, 0, 0.05) 90%,
                transparent 96%
        );
        mask-image: linear-gradient(
                to bottom,
                black 75%,
                rgba(0, 0, 0, 0.8) 78%,
                rgba(0, 0, 0, 0.6) 80%,
                rgba(0, 0, 0, 0.4) 82%,
                rgba(0, 0, 0, 0.2) 85%,
                rgba(0, 0, 0, 0.05) 90%,
                transparent 96%
        );
      }

      .message {
        font-size: 16px;
        line-height: 24px;
        padding: 6px 0;
        border-radius: 20px;
        margin-bottom: 5px;
      }

      .message.user {
        padding: 6px 12px;
      }
        
      .message.error svg {
        width: 32px;
        height: 32px;
      }

      .scroll-to-bottom-button {
        bottom: 10px;
        width: 38px;
        height: 38px;
      }

      .chat-input {
        padding: 10px 24px 0;
        column-gap: 16px;
      }

      .chat-input input {
        height: 26px;
        padding: 8px 20px;
        border-radius: 26px;
        font-size: 16px;
      }

      .send-button {
        padding: 12px;
        border-radius: 22px;
        width: 44px;
        height: 44px;
      }

      .loading {
        padding-left: 5px;
      }

      .typing-placeholder {
        font-size: 14px;
      }

      .disclaimer {
        font-size: 12px;
        padding: 8px 24px;
        line-height: 1.2;
      }

      .footer {
        font-size: 10px;
        padding: 0 24px 32px;
        line-height: 1.2;
      }

      .chat-data-loading {
        padding: 40px 20px;
      }

      .chat-data-loading-spinner {
        margin-bottom: 15px;
      }

      .chat-data-loading-spinner span {
        width: 12px;
        height: 12px;
        margin: 0 3px;
      }

      .chat-data-loading-text {
        font-size: 14px;
      }
    }

    @media (max-width: 1440px) {
      .info-box {
        font-size: 14px;
        line-height: 20px;
      }

      .chat-header {
        font-size: 18px;
        height: 68px;
      }

      .close-button {
        padding: 10px;
      }
        
      .chat-header-content {
        column-gap: 10px;
      }

      .chat-header-icon-wrapper {
        padding: 10px;
      }

      .chat-header-name {
        font-size: 14px;
        line-height: 18px;
      }

      .chat-header-availability {
        column-gap: 4px;
      }

      .chat-header-availability-icon {
        width: 8px;
        height: 8px;
      }

      .chat-header-availability-text {
        font-size: 11px;
        line-height: 14px;
      }

      .chat-messages {
        padding: 0 20px;
        gap: 8px;
      }

      .message {
        font-size: 14px;
        line-height: 20px;
      }

      .scroll-to-bottom-button {
        bottom: 10px;
        width: 38px;
        height: 38px;
      }

      .chat-input {
        padding: 8px 20px 0;
        column-gap: 12px;
      }

      .chat-input input {
        height: 22px;
        padding: 8px 16px;
        border-radius: 22px;
        font-size: 14px;
      }

      .send-button {
        padding: 10px;
        width: 40px;
        height: 40px;
      }

      .loading {
        padding-left: 5px;
      }

      .typing-placeholder {
        font-size: 14px;
      }

      .disclaimer {
        font-size: 11px;
        padding: 8px 20px 4px;
        line-height: 1.2;
      }

      .footer {
        font-size: 9px;
        padding: 0 20px 12px;
        line-height: 1.2;
      }

      .chat-data-loading {
        padding: 40px 20px;
      }

      .chat-data-loading-spinner {
        margin-bottom: 15px;
      }

      .chat-data-loading-spinner span {
        width: 12px;
        height: 12px;
        margin: 0 3px;
      }

      .chat-data-loading-text {
        font-size: 14px;
      }
    }

    @media (max-width: 768px) {
      :host {
        bottom: 15px;
        right: 15px;
      }

      .info-box {
        max-width: calc(100vw - 78px);
        bottom: 70px;
      }

      .chat {
        max-width: calc(100vw - 30px);
        bottom: 70px;
      }

      .chat-messages {
        padding: 0 16px;
      }

      .disclaimer {
        padding: 8px 16px 4px;
      }

      .footer {
        padding: 0 16px 12px;
      }
    }

    @media (max-width: 480px) {
      :host {
        bottom: 10px;
        right: 10px;
      }

      .info-box {
        max-width: calc(100vw - 68px);
      }

      .chat {
        max-width: calc(100vw - 20px);
      }

      .chat-input {
        padding: 8px 16px 0;
      }

    }

    .message.assistant h1,
    .message.assistant h2,
    .message.assistant h3,
    .message.assistant h4,
    .message.assistant h5,
    .message.assistant h6 {
      margin: 0.5em 0 0.3em 0;
      font-weight: 600;
    }

    .message.assistant h1 { font-size: 1.4em; }
    .message.assistant h2 { font-size: 1.3em; }
    .message.assistant h3 { font-size: 1.2em; }
    .message.assistant h4 { font-size: 1.1em; }
    .message.assistant h5 { font-size: 1.05em; }
    .message.assistant h6 { font-size: 1em; }

    .message.assistant p {
      margin: 0;
    }

    .message.assistant ul,
    .message.assistant ol {
      margin: 0.5em 0;
      padding-left: 1.5em;
    }

    .message.assistant li {
      margin: 0.2em 0;
      line-height: 1.3;
    }

    .message.assistant blockquote {
      margin: 0.5em 0;
      padding: 0.3em 0.8em;
      border-left: 3px solid var(--widget-primary-light);
      background-color: rgba(255, 255, 255, 0.5);
      border-radius: 0 4px 4px 0;
      font-style: italic;
    }

    .message.assistant code {
      background-color: rgba(255, 255, 255, 0.5);
      padding: 0.1em 0.3em;
      border-radius: 3px;
      font-size: 0.9em;
    }

    .message.assistant pre {
      background-color: rgba(255, 255, 255, 0.5);
      padding: 0.8em;
      border-radius: 4px;
      overflow-x: auto;
      margin: 0.5em 0;
    }

    .message.assistant pre code {
      background: none;
      padding: 0;
      font-size: 0.85em;
      line-height: 1.3;
    }

    .message.assistant strong {
      font-weight: 600;
    }

    .message.assistant em {
      font-style: italic;
    }

    .message.assistant a {
      color: var(--widget-font-light);
      text-decoration: underline;
    }

    .message.assistant table {
      border-collapse: collapse;
      width: 100%;
      margin: 0.5em 0;
      font-size: 0.9em;
    }

    .message.assistant th,
    .message.assistant td {
      border: 1px solid rgba(255, 255, 255, 0.25);
      padding: 0.3em 0.6em;
      text-align: left;
    }

    .message.assistant th {
      background-color: rgba(255, 255, 255, 0.5);
      font-weight: 600;
    }

    .message.assistant hr {
      border: none;
      border-top: 1px solid rgba(255, 255, 255, 0.25);
      margin: 1em 0;
    }

    /* Rating styles */
    .rating-box {
      position: absolute;
      bottom: 80px;
      right: 0;
      width: 500px;
      max-width: calc(100vw - 40px);
      background-color: rgba(51, 50, 50, 0.8);
      border: 1px solid rgba(118, 114, 113, 1);
      backdrop-filter: blur(2px);
      border-radius: 24px;
      padding: 24px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      z-index: 999998;
      opacity: 0;
      visibility: hidden;
      transform: translateY(30px) scale(0.9);
      transition: none;
      color: white;
      min-height: 180px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .rating-box.visible {
      opacity: 1;
      visibility: visible;
      animation: infoBoxSlideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    .rating-box.hiding {
      animation: fadeOut 0.3s ease-out forwards;
    }

    .rating-in-chat {
      background-color: rgba(51, 50, 50, 0.6);
      border: 1px solid rgba(118, 114, 113, 1);
      border-radius: 16px;
      padding: 20px;
      margin: 10px 0;
      text-align: center;
      color: white;
      transition: opacity 0.3s ease-out, transform 0.3s ease-out;
      position: relative;
      min-height: 140px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .rating-in-chat.hiding {
      opacity: 0;
      transform: translateY(-10px);
    }

    .rating-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
      color: white;
      transition: opacity 0.2s ease-out, max-height 0.2s ease-out, margin 0.2s ease-out;
      overflow: hidden;
      flex-shrink: 0;
    }

    .rating-title.hidden {
      opacity: 0;
      max-height: 0;
      margin-bottom: 0;
    }

    .rating-subtitle {
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 20px;
      color: rgba(255, 255, 255, 0.9);
      transition: opacity 0.2s ease-out, max-height 0.2s ease-out, margin 0.2s ease-out;
      overflow: hidden;
      flex-shrink: 0;
    }

    .rating-subtitle.hidden {
      opacity: 0;
      max-height: 0;
      margin-bottom: 0;
    }

    .rating-stars {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-bottom: 0;
      transition: margin 0.2s ease-out;
      flex-shrink: 0;
    }

    .rating-stars.centered {
      margin-bottom: 0;
    }

    .star {
      cursor: pointer;
      font-size: 32px;
      transition: all 0.15s ease;
      color: rgba(255, 255, 255, 0.3);
    }

    .star:hover {
      transform: scale(1.2);
    }

    .star.selected {
      color: #FFD700;
    }

    .rating-thank-you {
      font-size: 14px;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.9);
      text-align: center;
      opacity: 0;
      margin-top: 0;
      max-height: 0;
      overflow: hidden;
      transition: opacity 0.3s ease-in 0.15s, max-height 0.3s ease-in 0.15s, margin-top 0.3s ease-in 0.15s;
      flex-shrink: 0;
    }

    .rating-thank-you.visible {
      opacity: 1;
      max-height: 100px;
      margin-top: 16px;
    }

    .rating-dismiss {
      background: none;
      border: none;
      border-radius: 20px;
      color: rgba(255, 255, 255, 0.5);
      font-size: 12px;
      font-weight: 400;
      padding: 6px 12px;
      cursor: pointer;
      transition: all 0.15s ease;
      margin-top: 8px;
      align-self: center;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .rating-dismiss:hover {
      background: rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 0.9);
    }

    @media (max-width: 768px) {
      .rating-box {
        max-width: calc(100vw - 78px);
        bottom: 70px;
        min-height: 160px;
      }
      
      .rating-title {
        font-size: 14px;
        line-height: 1.3;
      }
      
      .rating-subtitle {
        font-size: 13px;
        line-height: 1.3;
        margin-bottom: 16px;
      }
      
      .star {
        font-size: 28px;
      }
      
      .rating-thank-you {
        font-size: 13px;
      }
      
      .rating-dismiss {
        font-size: 11px;
        padding: 5px 10px;
        margin-top: 6px;
      }
    }

    @media (max-width: 480px) {
      .rating-box {
        max-width: calc(100vw - 68px);
        padding: 20px 16px;
        min-height: 140px;
      }
      
      .rating-title {
        font-size: 13px;
        line-height: 1.2;
      }
      
      .rating-subtitle {
        font-size: 12px;
        line-height: 1.2;
        margin-bottom: 14px;
      }
      
      .star {
        font-size: 24px;
      }
      
      .rating-thank-you {
        font-size: 12px;
      }

      .rating-in-chat {
        padding: 16px;
        min-height: 120px;
      }
      
      .rating-dismiss {
        font-size: 10px;
        padding: 4px 8px;
        margin-top: 4px;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._previousDomainApiUrl = this.domainapiurl;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.microphoneAccessDenied = true;
    } else {
      this.microphoneAccessDenied = false;
    }

    this.initChat();
    this.initAttentionGrabbers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.disconnectSocket();
    this.cleanupAttentionGrabbers();
    this.stopTypingAnimation();
    this.clearResponseTimeout();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('domainapiurl') && this.domainapiurl !== this._previousDomainApiUrl) {
      this._previousDomainApiUrl = this.domainapiurl;
      this.initChat();
    }

    if (changedProperties.has('open') && this.open) {
      this.scrollToBottomIfOpen();
    }
    if (changedProperties.has('chatDataLoading') && !this.chatDataLoading && this.open) {
      this.scrollToBottomIfOpen();
    }
  }

  resetChat() {
    this.chat = { messages: [], shared: false };
    this.loading = false;
    this.stopTypingAnimation();
    this.clearResponseTimeout();
    this.disconnectSocket();
    this.questionAnswerCount = 0;
    this.sessionRated = false;
    this.showRatingInChat = false;
    this.showRatingPopup = false;
    this.selectedRating = 0;
    this.ratingSubmitted = false;
    this.ratingThankYouMessage = false;
    this.ratingInChatHiding = false;
    this.ratingPopupHiding = false;
    this.ratingInChatDismissed = false;
  }

  private getRandomInterval(min: number, max: number): number {
    return Math.random() * (max - min) + min;
  }

  startTypingAnimation() {
    this.stopTypingAnimation();
    this.typingPhase = 1;
    this.typingText = 'Analizuję pytanie…';
    this.requestUpdate();

    // Losowe czasy między 1.5s a 2.5s dla każdego przejścia
    const time1 = this.getRandomInterval(1500, 2500); // 1.5-2.5s
    const time2 = this.getRandomInterval(1500, 2500); // 1.5-2.5s
    const time3 = this.getRandomInterval(1500, 2500); // 1.5-2.5s
    const time4 = this.getRandomInterval(1500, 2500); // 1.5-2.5s
    const time5 = this.getRandomInterval(1500, 2500); // 1.5-2.5s

    this.typingTimer = setTimeout(() => {
      if (this.typingPhase === 1) {
        this.typingPhase = 2;
        this.typingText = 'Zbieram potrzebne informacje…';
        this.requestUpdate();
      }
    }, time1);

    this.typingTimer2 = setTimeout(() => {
      if (this.typingPhase === 2) {
        this.typingPhase = 3;
        this.typingText = 'Łączę dane z różnych źródeł…';
        this.requestUpdate();
      }
    }, time1 + time2);

    this.typingTimer3 = setTimeout(() => {
      if (this.typingPhase === 3) {
        this.typingPhase = 4;
        this.typingText = 'Sprawdzam, czy wszystko się zgadza…';
        this.requestUpdate();
      }
    }, time1 + time2 + time3);

    this.typingTimer4 = setTimeout(() => {
      if (this.typingPhase === 4) {
        this.typingPhase = 5;
        this.typingText = 'Porządkuję odpowiedź, żeby była jasna i konkretna…';
        this.requestUpdate();
      }
    }, time1 + time2 + time3 + time4);

    this.typingTimer5 = setTimeout(() => {
      if (this.typingPhase === 5) {
        this.typingPhase = 6;
        this.typingText = 'Jeszcze chwila — dopracowuję szczegóły…';
        this.requestUpdate();
      }
    }, time1 + time2 + time3 + time4 + time5);
  }

  stopTypingAnimation() {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
    if (this.typingTimer2) {
      clearTimeout(this.typingTimer2);
      this.typingTimer2 = null;
    }
    if (this.typingTimer3) {
      clearTimeout(this.typingTimer3);
      this.typingTimer3 = null;
    }
    if (this.typingTimer4) {
      clearTimeout(this.typingTimer4);
      this.typingTimer4 = null;
    }
    if (this.typingTimer5) {
      clearTimeout(this.typingTimer5);
      this.typingTimer5 = null;
    }
    this.typingPhase = null;
    this.typingText = '';
  }

  startResponseTimeout() {
    this.clearResponseTimeout();
    this.hasTimeoutError = false;

    this.responseTimeoutTimer = setTimeout(() => {
      if (this.loading) {
        this.handleResponseTimeout();
      }
    }, 60000);
  }

  clearResponseTimeout() {
    if (this.responseTimeoutTimer) {
      clearTimeout(this.responseTimeoutTimer);
      this.responseTimeoutTimer = null;
    }
  }

  handleResponseTimeout() {
    this.loading = false;
    this.stopTypingAnimation();

    const lastMsg = this.chat.messages[this.chat.messages.length - 1];
    if (lastMsg && lastMsg.type === WidgetModel.MessageType.TYPING) {
      this.chat.messages.pop();
    }

    this.chat = {
      ...this.chat,
      messages: [
        ...this.chat.messages,
        {
          author: WidgetModel.Author.ASSISTANT,
          text: 'Przepraszam, odpowiedź zajmuje dłużej niż oczekiwano. Spróbuj ponownie.',
          type: WidgetModel.MessageType.ERROR,
        }
      ]
    };

    this.hasTimeoutError = true;
    this.requestUpdate();
    setTimeout(() => this.scrollToBottom(), 100);
  }

  disconnectSocket() {
    this.stopHeartbeat();

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  async initChat() {
    try {
      this.chatDataLoading = true;
      this.requestUpdate();
      this.resetChat();

      let chatInfo: WidgetModel.BasicChatDTO | null = null;
      let welcomeMessage = this.welcomemessage;
      let infoMessage = this.infomessage;

      try {
        const chatResponse = await fetch(`${this.domainapiurl}/shared-chat/widget`);
        if (chatResponse.ok) {
          const chatText = await chatResponse.text();
          if (chatText.trim() !== '') {
            chatInfo = JSON.parse(chatText) as WidgetModel.BasicChatDTO;

            if (chatInfo?.maintanance_mode) {
              this.maintananceMode = true;
              this.chatDataLoading = false;
              this.scrollToBottomIfOpen();
              return;
            }

            if (chatInfo?.welcome_message) {
              welcomeMessage = chatInfo.welcome_message;
            }

            if (chatInfo?.info_message) {
              infoMessage = chatInfo.info_message;
            }
          }
        } else {
          console.error(`Error: ${chatResponse.status}`);
          this.maintananceMode = true;
          this.chatDataLoading = false;
          this.scrollToBottomIfOpen();
          return;
        }
      } catch (error) {
        console.error('Error:', error);
        this.maintananceMode = true;
        this.chatDataLoading = false;
        this.scrollToBottomIfOpen();
        return;
      }

      this.fetchedInfoMessage = infoMessage;

      const savedSessionId = this.cookiesService.getSessionCookie();
      if (savedSessionId) {
        this.chat_session_id = parseInt(savedSessionId);
      }

      if (this.chat_session_id && this.domainapiurl) {
        const fetchUrl = `${this.domainapiurl}/shared-chat/session/${this.chat_session_id}`;

        try {
          const response = await fetch(fetchUrl);

          if (response.ok) {
            const text = await response.text();
            if (text.trim() === '') {
              throw new Error('Received empty JSON response');
            }

            try {
              const sessionData = JSON.parse(text) as WidgetModel.ChatSessionDTO;
              this.chat = {
                id: sessionData.chat?.id,
                created_at: sessionData.chat?.created_at,
                modified_at: sessionData.chat?.modified_at,
                name: sessionData.chat?.name,
                description: sessionData.chat?.description,
                messages: sessionData.messages || [],
                shared: true,
                files: []
              };
              
              this.sessionRated = sessionData.is_rated || false;
              
              this.countQuestionAnswerPairs();
              
              this.requestUpdate();
              if (this.open) {
                setTimeout(() => this.scrollToBottom(), 100);
              }
            } catch (jsonError) {
              throw new Error(`JSON parse error: ${(jsonError as Error).message}`);
            }
          } else {
            if (response.status === 404) {
              this.cookiesService.clearSessionCookie();
              this.chat_session_id = null;
            }
            throw new Error(`Failed to fetch chat session data, status: ${response.status}`);
          }
        } catch (error) {
          console.warn('Error loading chat session, starting with empty chat:', error);
          this.chat = { messages: [], shared: false };
        }
      } else {
        this.chat = { messages: [], shared: false };
      }

      if (!this.chat.messages) {
        this.chat.messages = [];
      }

      const hasInfoMessage = this.chat.messages.length > 0 &&
        this.chat.messages[0].type === WidgetModel.MessageType.INFO_MESSAGE;

      const hasWelcomeMessage = this.chat.messages.some((msg, index) =>
        index <= 1 && msg.type === WidgetModel.MessageType.CORE_MESSAGE && 
        (msg.text === welcomeMessage || (hasInfoMessage && index === 1))
      );

      const messagesToAdd = [];
      
      if (infoMessage && infoMessage.trim() !== '' && !hasInfoMessage) {
        messagesToAdd.push({
          author: WidgetModel.Author.ASSISTANT,
          text: infoMessage,
          type: WidgetModel.MessageType.INFO_MESSAGE,
        });
      }
      
      if (!hasWelcomeMessage) {
        messagesToAdd.push({
          author: WidgetModel.Author.ASSISTANT,
          text: welcomeMessage,
          type: WidgetModel.MessageType.CORE_MESSAGE,
        });
      }

      if (messagesToAdd.length > 0) {
        this.chat.messages = [...messagesToAdd, ...this.chat.messages];
      }

      this.chatDataLoading = false;
      this.requestUpdate();

      this.scrollToBottomIfOpen();

      this.checkAndShowInfoBox(infoMessage);

      await this.connectWebSocket();
    } catch (error) {
      console.error('Error during chat initialization:', error);
      this.chatDataLoading = false;
      this.scrollToBottomIfOpen();
    }
  }

  startHeartbeat() {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.open && this.socket && this.socket.connected) {
        this.socket.emit('ping', { timestamp: Date.now() });

        this.heartbeatTimeout = setTimeout(() => {
          this.reconnectSocket();
        }, 5000);
      }
    }, 15000);

    if (this.open && this.socket && this.socket.connected) {
      this.socket.emit('ping', { timestamp: Date.now() });
    }
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  handlePong() {
    this.lastPongReceived = Date.now();

    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  reconnectSocket() {
    this.connectWebSocket();
  }

  connectWebSocket() {
    return new Promise<boolean>(resolve => {
      try {
        this.disconnectSocket();

        const socket = io(this.domainapiurl, {
          transports: ['websocket', 'polling'],
          forceNew: true,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          timeout: 20000,
        });

        let connectionTimeout = setTimeout(() => {
          console.error('WebSocket connection timeout exceeded');
          resolve(false);
        }, 10000);

        socket.on('connect', () => {
          clearTimeout(connectionTimeout);
          this.socket = socket;
          this.startHeartbeat();
          resolve(true);
        });

        socket.on('connect_error', error => {
          console.error('WebSocket connection error:', error);
        });

        socket.on('disconnect', reason => {
          this.stopHeartbeat();
          if (reason === 'io server disconnect' || reason === 'transport close') {
            socket.connect();
          }
        });

        // Heartbeat pong response
        socket.on('pong', () => {
          this.handlePong();
        });

        socket.on('widget-question', (data: WidgetModel.Message) => {
          this.handleSocketMessage(data);
        });

        socket.on('widget-error', (data: any) => {
          this.handleErrorMessage(data);
        });
      } catch (error) {
        resolve(false);
      }
    });
  }

  handleSocketMessage(message: WidgetModel.Message) {
    if (this.hasTimeoutError) {
      const lastMsg = this.chat.messages[this.chat.messages.length - 1];
      if (lastMsg && lastMsg.type === WidgetModel.MessageType.ERROR) {
        this.chat.messages.pop();
      }
      this.hasTimeoutError = false;
      this.clearResponseTimeout();
    }

    switch (message.type) {
      case WidgetModel.MessageType.CORE_MESSAGE: {
        const lastMsg = this.chat.messages[this.chat.messages.length - 1];

        if (
          lastMsg &&
          lastMsg.author === WidgetModel.Author.ASSISTANT &&
          lastMsg.type === WidgetModel.MessageType.CORE_MESSAGE
        ) {
          if (!message.final) {
            lastMsg.text += message.text;
          }
        } else if (lastMsg && lastMsg.type === WidgetModel.MessageType.TYPING && lastMsg.text === '') {
          lastMsg.type = WidgetModel.MessageType.CORE_MESSAGE;
          lastMsg.text = message.text;
        } else {
          this.chat = {
            ...this.chat,
            messages: [
              ...this.chat.messages,
              {
            author: WidgetModel.Author.ASSISTANT,
            text: message.text,
            type: WidgetModel.MessageType.CORE_MESSAGE,
              }
            ]
          };
        }

        if (!message.final) {
          this.loading = true;
        } else {
          this.loading = false;
          this.stopTypingAnimation();
          this.clearResponseTimeout();
          
          this.checkShouldShowRating();
        }

        if (message.chat_session_id) {
          if (!this.chat_session_id) {
            this.chat_session_id = message.chat_session_id;
            this.cookiesService.setSessionCookie(message.chat_session_id);
          } else if (this.chat_session_id !== message.chat_session_id) {
            this.chat_session_id = message.chat_session_id;
            this.cookiesService.setSessionCookie(message.chat_session_id);
          }
        }

        this.scrollToBottom();
        this.requestUpdate();
        break;
      }
    }
    this.requestUpdate();
  }

  handleErrorMessage(data: any) {
    this.loading = false;
    this.stopTypingAnimation();
    this.clearResponseTimeout();

    const lastMsg = this.chat.messages[this.chat.messages.length - 1];
    if (lastMsg && lastMsg.type === WidgetModel.MessageType.TYPING) {
      this.chat.messages.pop();
    }

    const errorMessage = data.message || data.text || 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.';

    this.chat = {
      ...this.chat,
      messages: [
        ...this.chat.messages,
        {
          author: WidgetModel.Author.ASSISTANT,
          text: errorMessage,
          type: WidgetModel.MessageType.ERROR,
        }
      ]
    };

    this.requestUpdate();
    setTimeout(() => this.scrollToBottom(), 100);
  }

  countQuestionAnswerPairs() {
    let count = 0;
    const messages = this.chat.messages.filter(
      msg => msg.type === WidgetModel.MessageType.CORE_MESSAGE
    );
    
    for (let i = 0; i < messages.length - 1; i++) {
      if (
        messages[i].author === WidgetModel.Author.USER &&
        messages[i + 1].author === WidgetModel.Author.ASSISTANT
      ) {
        count++;
      }
    }
    
    this.questionAnswerCount = count;
  }

  checkShouldShowRating() {
    if (this.sessionRated || this.ratingSubmitted) {
      return;
    }

    this.countQuestionAnswerPairs();

    if (this.questionAnswerCount >= 5 && !this.showRatingInChat && !this.ratingInChatDismissed) {
      this.showRatingInChat = true;
      this.requestUpdate();
      
      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    }
  }

  toggleChat() {
    const wasOpen = this.open;
    this.open = !this.open;
    
    if (this.open) {
      this.hideCTA();
      this.showRatingPopup = false;

      const today = new Date().toDateString();
      this.cookiesService.setCookie('chat-opened', today, 1); // 1 day

      if (this.socket && this.socket.connected) {
        this.startHeartbeat();
      }

      if (!this.chatDataLoading) {
        setTimeout(() => this.scrollToBottom(), 100);
      }
      setTimeout(() => this.scrollToBottom(), 300);
    } else {
      this.stopHeartbeat();
      
      if (wasOpen && !this.sessionRated && !this.ratingSubmitted && this.questionAnswerCount >= 1) {
        setTimeout(() => {
          this.showRatingPopup = true;
          this.requestUpdate();
        }, 500);
      }
    }
  }

  scrollToBottom() {
    const chatMessages = this.shadowRoot?.querySelector('.chat-messages');
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }
  }

  scrollToBottomIfOpen() {
    if (this.open && !this.chatDataLoading) {
      setTimeout(() => this.scrollToBottom(), 50);
    }
  }

  checkIfScrolledToBottom() {
    const chatMessages = this.shadowRoot?.querySelector('.chat-messages');
    if (chatMessages) {
      const threshold = 50;
      const isAtBottom = chatMessages.scrollHeight - chatMessages.scrollTop - chatMessages.clientHeight <= threshold;
      const isAtTop = chatMessages.scrollTop === 0;
      this.isScrolledToBottom = isAtBottom;
      this.isScrolledToTop = isAtTop;
      this.requestUpdate();
    }
  }

  handleChatScroll() {
    this.checkIfScrolledToBottom();
  }

  initAttentionGrabbers() {
    this.startPulseAnimation();
  }

  checkAndShowInfoBox(infoMessage: string) {
    const chatOpened = this.cookiesService.getCookie('chat-opened');
    const infoBoxClosed = this.cookiesService.getCookie('info-box-closed');
    const today = new Date().toDateString();


    if (chatOpened !== today && infoBoxClosed !== today && infoMessage && infoMessage.trim() !== '') {
      this.ctaTimeout = setTimeout(() => {
        if (!this.open) {
          this.showInfoBox = true;
          this.requestUpdate();
        }
      }, 5000);
    }
  }

  closeInfoBox() {
    this.showInfoBox = false;
    const today = new Date().toDateString();
    this.cookiesService.setCookie('info-box-closed', today, 1); // 1 day
    this.requestUpdate();
  }

  startPulseAnimation() {
    this.pulseInterval = setInterval(() => {
      if (!this.open && !this.showInfoBox) {
        this.isPulsing = true;
        this.requestUpdate();
        setTimeout(() => {
          this.isPulsing = false;
          this.requestUpdate();
        }, 600);
      }
    }, 3000);
  }

  cleanupAttentionGrabbers() {
    if (this.pulseInterval) {
      clearInterval(this.pulseInterval);
      this.pulseInterval = null;
    }
    if (this.ctaTimeout) {
      clearTimeout(this.ctaTimeout);
      this.ctaTimeout = null;
    }
  }

  hideCTA() {
    this.showInfoBox = false;
    this.requestUpdate();
  }

  handleInput(e: InputEvent) {
    const target = e.target as HTMLInputElement;
    this.messageInput = target.value;
  }

  async sendMessage(e?: Event) {
    if (e) {
      e.preventDefault();
    }

    if (this.showRatingInChat && !this.ratingSubmitted) {
      return;
    }

    if (this.messageInput.trim() !== '' && !this.loading && !this.chatDataLoading) {
      this.loading = true;

      this.chat = {
        ...this.chat,
        messages: [
          ...this.chat.messages,
          {
        author: WidgetModel.Author.USER,
        text: this.messageInput,
        type: WidgetModel.MessageType.CORE_MESSAGE,
          }
        ]
      };

      this.messageInput = '';

      this.requestUpdate();
      this.scrollToBottom();

      try {
        if (this.socket) {
          this.socket.emit('widget-question', {
            text: this.chat.messages[this.chat.messages.length - 1].text,
            ...(this.chat_session_id ? { chat_session: { id: this.chat_session_id } } : {}),
            ...(!this.chat_session_id && this.chat.messages.length >= 2
              ? { welcome_message: this.welcomemessage }
              : {}),
          });

          this.chat = {
            ...this.chat,
            messages: [
              ...this.chat.messages,
              {
                author: WidgetModel.Author.ASSISTANT,
                text: '',
                type: WidgetModel.MessageType.TYPING,
               }
            ]
          };

          this.startTypingAnimation();
          this.startResponseTimeout();
          this.requestUpdate();
          setTimeout(() => this.scrollToBottom(), 100);
        } else {
          console.error('No WebSocket connection available to send message');
          this.loading = false;

          this.chat = {
            ...this.chat,
            messages: [
              ...this.chat.messages,
              {
            author: WidgetModel.Author.ASSISTANT,
            text: 'Sorry, there was a connection problem. Please refresh the page and try again.',
            type: WidgetModel.MessageType.CORE_MESSAGE,
              }
            ]
          };
        }
      } catch (error) {
        this.loading = false;

        this.chat = {
          ...this.chat,
          messages: [
            ...this.chat.messages,
            {
          author: WidgetModel.Author.ASSISTANT,
          text: 'Sorry, there was a problem sending the message. Please try again.',
          type: WidgetModel.MessageType.CORE_MESSAGE,
            }
          ]
        };
      }
    }
  }

  selectRating(rating: number) {
    this.selectedRating = rating;
    this.ratingSubmitted = true;
    this.sessionRated = true;
    this.requestUpdate();
    
    this.submitRating();
    
    setTimeout(() => {
      this.ratingThankYouMessage = true;
      this.requestUpdate();
      
      setTimeout(() => {
        if (this.showRatingInChat) {
          this.ratingInChatHiding = true;
          this.requestUpdate();
          
          setTimeout(() => {
            this.showRatingInChat = false;
            this.ratingInChatHiding = false;
            this.ratingThankYouMessage = false;
            this.requestUpdate();
          }, 500);
        }
        
        if (this.showRatingPopup) {
          this.ratingPopupHiding = true;
          this.requestUpdate();
          
          setTimeout(() => {
            this.showRatingPopup = false;
            this.ratingPopupHiding = false;
            this.ratingThankYouMessage = false;
            this.requestUpdate();
          }, 500);
        }
      }, 2500);
    }, 300);
  }

  async submitRating() {
    if (this.selectedRating === 0 || !this.chat_session_id) {
      return;
    }

    try {
      await fetch(`${this.domainapiurl}/shared-chat/session/${this.chat_session_id}/rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating: this.selectedRating,
        }),
      });
    } catch (error) {
      console.error('Error submitting rating:', error);
    }
  }

  dismissRatingInChat() {
    this.ratingInChatHiding = true;
    this.requestUpdate();
    
    setTimeout(() => {
      this.showRatingInChat = false;
      this.ratingInChatHiding = false;
      this.ratingInChatDismissed = true;
      this.requestUpdate();
    }, 500);
  }

  render() {
    if (this.maintananceMode) {
      return html``;
    }

    return html`
      <div class="widget-button ${this.isPulsing ? 'pulsing' : ''}" @click=${this.toggleChat}>
        <div class="widget-button-content">
          <div class="widget-button-icon-wrapper">
            ${this.open 
              ? html`<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.64136 2.05664C1.88551 1.81257 2.28198 1.81256 2.52612 2.05664L10.0261 9.55664C10.2695 9.80164 10.2693 10.1982 10.0251 10.4424L2.52515 17.9424C2.4036 18.0639 2.24356 18.1249 2.08374 18.125C1.92375 18.125 1.76302 18.0649 1.64136 17.9424C1.39719 17.6982 1.3972 17.3018 1.64136 17.0576L8.69995 10L1.64136 2.94141C1.39726 2.69723 1.39721 2.30079 1.64136 2.05664Z" fill="#262626"/>
                <path d="M16.8496 2.05664C17.0937 1.81299 17.4894 1.81285 17.7334 2.05664C17.9776 2.30081 17.9776 2.69724 17.7334 2.94141L10.6748 10L17.7334 17.0576C17.9776 17.3018 17.9776 17.6982 17.7334 17.9424C17.6118 18.0647 17.4518 18.1249 17.292 18.125C17.132 18.125 16.9713 18.064 16.8496 17.9424L9.34961 10.4424C9.10551 10.1982 9.1063 9.80162 9.34961 9.55664L16.8496 2.05664Z" fill="#262626"/>
              </svg>` : html`<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 5.25H15.75V4C15.75 1.932 14.068 0.25 12 0.25H4C1.932 0.25 0.25 1.932 0.25 4V14C0.25 14.305 0.435003 14.579 0.716003 14.694C0.808003 14.732 0.904 14.75 1 14.75C1.198 14.75 1.39201 14.672 1.53601 14.525L3.27499 12.75H4.25V14C4.25 16.068 5.932 17.75 8 17.75H16.725L18.464 19.525C18.608 19.672 18.802 19.75 19 19.75C19.096 19.75 19.192 19.732 19.284 19.694C19.566 19.579 19.75 19.304 19.75 19V9C19.75 6.932 18.068 5.25 16 5.25ZM2.95999 11.25C2.75799 11.25 2.56501 11.331 2.42401 11.475L1.75 12.163V4C1.75 2.759 2.759 1.75 4 1.75H12C13.241 1.75 14.25 2.759 14.25 4V9C14.25 10.241 13.241 11.25 12 11.25H2.95999ZM18.25 17.163L17.576 16.475C17.435 16.331 17.242 16.25 17.04 16.25H8C6.759 16.25 5.75 15.241 5.75 14V12.75H12C14.068 12.75 15.75 11.068 15.75 9V6.75H16C17.241 6.75 18.25 7.759 18.25 9V17.163Z" fill="#262626"/>
            </svg>`}
          </div>
          
          <div class="widget-button-text">Zapytaj Asystenta</div>
        </div>
      </div>
      ${this.showInfoBox && this.fetchedInfoMessage ? html`
        <div class="info-box ${this.showInfoBox ? 'visible' : ''}">
          <div class="info-box-wrapper">
            <div class="info-box-content">
              <button class="info-box-close" @click=${this.closeInfoBox} title="Zamknij">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.64209 2.05762C1.88626 1.81345 2.28171 1.81345 2.52588 2.05762L10.0259 9.55762C10.269 9.80263 10.269 10.1983 10.0249 10.4424L2.5249 17.9424C2.40329 18.0639 2.24338 18.1259 2.0835 18.126C1.92362 18.126 1.76372 18.0647 1.64209 17.9424C1.39792 17.6982 1.39792 17.3018 1.64209 17.0576L8.69971 10L1.64209 2.94141C1.39793 2.69724 1.39794 2.30179 1.64209 2.05762Z" fill="white"/>
                  <path d="M16.8492 2.05859C17.0933 1.81443 17.4888 1.81443 17.733 2.05859C17.9771 2.30276 17.9771 2.69823 17.733 2.94238L10.6754 10.001L17.733 17.0586C17.9771 17.3028 17.9771 17.6992 17.733 17.9434C17.6113 18.0657 17.4515 18.127 17.2916 18.127C17.1317 18.1269 16.9718 18.0649 16.8502 17.9434L9.35016 10.4434C9.10605 10.1993 9.10603 9.80361 9.34918 9.55859L16.8492 2.05859Z" fill="white"/>
                </svg>
              </button>
              ${this.fetchedInfoMessage}
            </div>
          </div>
        </div>
      ` : ''}
      ${this.showRatingPopup ? html`
        <div class="rating-box ${this.showRatingPopup ? 'visible' : ''} ${this.ratingPopupHiding ? 'hiding' : ''}">
          <div class="rating-title ${this.ratingThankYouMessage ? 'hidden' : ''}">Jak oceniasz rozmowę?</div>
          <div class="rating-subtitle ${this.ratingThankYouMessage ? 'hidden' : ''}">Twoja opinia pomoże mi się rozwijać.</div>
          <div class="rating-stars ${this.ratingThankYouMessage ? 'centered' : ''}">
            ${[1, 2, 3, 4, 5].map(star => html`
              <span 
                class="star ${this.selectedRating >= star ? 'selected' : ''}"
                @click=${() => this.selectRating(star)}
              >
                ★
              </span>
            `)}
          </div>
          <div class="rating-thank-you ${this.ratingThankYouMessage ? 'visible' : ''}">
            Dzięki Twojej opinii mogę być lepszy. Dziękuję!
          </div>
        </div>
      ` : ''}
      <div class="chat ${this.open ? 'open' : ''}">
        <div class="chat-header">
          <div class="close-button" @click=${this.toggleChat}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.64136 2.05664C1.88551 1.81257 2.28198 1.81256 2.52612 2.05664L10.0261 9.55664C10.2695 9.80164 10.2693 10.1982 10.0251 10.4424L2.52515 17.9424C2.4036 18.0639 2.24356 18.1249 2.08374 18.125C1.92375 18.125 1.76302 18.0649 1.64136 17.9424C1.39719 17.6982 1.3972 17.3018 1.64136 17.0576L8.69995 10L1.64136 2.94141C1.39726 2.69723 1.39721 2.30079 1.64136 2.05664Z" fill="white"/>
              <path d="M16.8496 2.05664C17.0937 1.81299 17.4894 1.81285 17.7334 2.05664C17.9776 2.30081 17.9776 2.69724 17.7334 2.94141L10.6748 10L17.7334 17.0576C17.9776 17.3018 17.9776 17.6982 17.7334 17.9424C17.6118 18.0647 17.4518 18.1249 17.292 18.125C17.132 18.125 16.9713 18.064 16.8496 17.9424L9.34961 10.4424C9.10551 10.1982 9.1063 9.80162 9.34961 9.55664L16.8496 2.05664Z" fill="white"/>
            </svg>
          </div>
          <div class="chat-header-content">
            <div class="chat-header-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 7.25H17.75V6C17.75 3.932 16.068 2.25 14 2.25H6C3.932 2.25 2.25 3.932 2.25 6V16C2.25 16.305 2.435 16.579 2.716 16.694C2.808 16.732 2.904 16.75 3 16.75C3.198 16.75 3.39201 16.672 3.53601 16.525L5.27499 14.75H6.25V16C6.25 18.068 7.932 19.75 10 19.75H18.725L20.464 21.525C20.608 21.672 20.802 21.75 21 21.75C21.096 21.75 21.192 21.732 21.284 21.694C21.566 21.579 21.75 21.304 21.75 21V11C21.75 8.932 20.068 7.25 18 7.25ZM4.95999 13.25C4.75799 13.25 4.56501 13.331 4.42401 13.475L3.75 14.163V6C3.75 4.759 4.759 3.75 6 3.75H14C15.241 3.75 16.25 4.759 16.25 6V11C16.25 12.241 15.241 13.25 14 13.25H4.95999ZM20.25 19.163L19.576 18.475C19.435 18.331 19.242 18.25 19.04 18.25H10C8.759 18.25 7.75 17.241 7.75 16V14.75H14C16.068 14.75 17.75 13.068 17.75 11V8.75H18C19.241 8.75 20.25 9.759 20.25 11V19.163Z" fill="#262626"/>
              </svg>
            </div>
            <div class="chat-header-title-wrapper">
              <div class="chat-header-name">
                Asystent Programu NORA®
              </div>
              
              <div class="chat-header-availability">
                <div class="chat-header-availability-icon"></div>
                <div class="chat-header-availability-text">Zawsze dostępny</div>
              </div>
            </div>
          </div>
        </div>
        <div class="chat-messages-wrapper">
          <div class="chat-messages ${!this.isScrolledToBottom ? 'not-at-bottom' : ''} ${this.isScrolledToTop ? 'at-top' : ''}" @scroll=${this.handleChatScroll}>
            ${this.chatDataLoading 
              ? html`
                <div class="chat-data-loading">
                  <div class="chat-data-loading-spinner">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              `
              : html`
                <div class="chat-messages-spacer"></div>
                ${this.chat.messages.map(
                message =>
                  html`
                    ${message.author === WidgetModel.Author.ASSISTANT && message.type === WidgetModel.MessageType.TYPING && message.text === '' ? html`<div class="typing-placeholder">${this.typingText}</div>` : html`
                    <div
                      class="message-container ${message.author === WidgetModel.Author.USER
                        ? 'user'
                        : 'assistant'}"
                    >
                      <div>
                        <div
                          class="message ${message.author === WidgetModel.Author.USER
                            ? 'user'
                            : message.type === WidgetModel.MessageType.ERROR 
                              ? 'error'
                              : message.type === WidgetModel.MessageType.INFO_MESSAGE
                                ? 'info'
                                : 'assistant'}"
                        >
                          ${message.author === WidgetModel.Author.ASSISTANT
                            ? message.type === WidgetModel.MessageType.TYPING && message.text === ''
                              ? '' 
                              : html`${message.type === WidgetModel.MessageType.ERROR ? html`<svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#ff0000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M12 8V12" stroke="#ff0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M12 16.0195V16" stroke="#ff0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <circle cx="12" cy="12" r="10" stroke="#ff0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></circle> </g></svg> <div class="content">${unsafeHTML(this.renderMarkdown(message.text))}</div>` : unsafeHTML(this.renderMarkdown(message.text))}`
                            : message.text}
                        </div>
                      </div>
                    </div>
                    `}
                  `
              )}
              ${this.showRatingInChat ? html`
                <div class="rating-in-chat ${this.ratingInChatHiding ? 'hiding' : ''}">
                  <div class="rating-title ${this.ratingThankYouMessage ? 'hidden' : ''}">Jak oceniasz rozmowę?</div>
                  <div class="rating-subtitle ${this.ratingThankYouMessage ? 'hidden' : ''}">Twoja opinia pomoże mi się rozwijać.</div>
                  <div class="rating-stars ${this.ratingThankYouMessage ? 'centered' : ''}">
                    ${[1, 2, 3, 4, 5].map(star => html`
                      <span 
                        class="star ${this.selectedRating >= star ? 'selected' : ''}"
                        @click=${() => this.selectRating(star)}
                      >
                        ★
                      </span>
                    `)}
                  </div>
                  <div class="rating-thank-you ${this.ratingThankYouMessage ? 'visible' : ''}">
                    Dzięki Twojej opinii mogę być lepszy. Dziękuję!
                  </div>
                  ${!this.ratingThankYouMessage ? html`
                    <button class="rating-dismiss" @click=${this.dismissRatingInChat}>
                      Oceń później
                    </button>
                  ` : ''}
                </div>
              ` : ''}
              `}
          </div>
          ${!this.isScrolledToBottom ? html`
            <button class="scroll-to-bottom-button ${!this.isScrolledToBottom ? 'visible' : ''}" @click=${this.scrollToBottom}>
              <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.64 6.24L1.504 5.392L6.208 10.064V0.799999H7.52V10.032L12.208 5.376L13.072 6.24L7.216 12H6.496L0.64 6.24Z" fill="black"/>
              </svg>
            </button>
          ` : ''}
        </div>
        <div class="chat-input">
          <div class="chat-input-wrapper">
            <input
              type="text"
              placeholder="Zapytaj o cokolwiek..."
              .value=${this.messageInput}
              @input=${this.handleInput}
              @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.sendMessage()}
                ?disabled=${this.loading || this.chatDataLoading || (this.showRatingInChat && !this.ratingSubmitted)}
            />
          </div>
          
          <button
            class="send-button"
            @click=${this.sendMessage}
            ?disabled=${this.loading || this.messageInput === '' || this.chatDataLoading || (this.showRatingInChat && !this.ratingSubmitted)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.9269 3.07294C20.2239 2.36994 19.2288 2.094 18.2638 2.336L5.15086 5.61395C3.40286 6.05095 2.23686 7.555 2.24986 9.357C2.26286 11.159 3.44887 12.6459 5.20287 13.0589L9.84886 14.1519L10.9419 18.7979C11.3549 20.5519 12.8418 21.7379 14.6438 21.7509C14.6538 21.7509 14.6628 21.7509 14.6728 21.7509C16.4618 21.7509 17.9519 20.5879 18.3869 18.8499L21.6648 5.737C21.9058 4.771 21.6299 3.77594 20.9269 3.07294ZM20.2088 5.37091L16.9309 18.4839C16.6019 19.7959 15.4949 20.2349 14.6539 20.2499C13.8089 20.2439 12.7118 19.7699 12.4018 18.4529L11.2978 13.7629L15.5299 9.53095C15.8229 9.23795 15.8229 8.76291 15.5299 8.46991C15.2369 8.17691 14.7619 8.17691 14.4689 8.46991L10.2369 12.702L5.54686 11.598C4.22986 11.288 3.75586 10.1909 3.74986 9.34589C3.74386 8.49989 4.20285 7.39691 5.51585 7.06891L18.6279 3.79096C18.7349 3.76396 18.8428 3.75092 18.9488 3.75092C19.2888 3.75092 19.6169 3.885 19.8659 4.133C20.1929 4.46 20.3208 4.92291 20.2088 5.37091Z" fill="white"/>
            </svg>
          </button>
        </div>
        <div class="disclaimer">
          Nie ponosimy odpowiedzialności za dane osobowe i poufne informacje przekazywane
          przez użytkowników w tym czacie.
        </div>
        <div class="footer">
          Powered by
          <a class="footer-link" target="_blank" href="https://g-group.dev">G-Group.dev</a>
        </div>
      </div>
    `;
  }
}
